// @ts-check
// Automações de gatilho manual (contrato 08, seção 3.6): aparecem no menu de comandos (registro aberto ou seleção da
// tabela) e no menu "mais" da barra de seleção, sempre com confirmação. A lista vem do servidor já filtrada pelo papel
// de quem está logado; o servidor confere de novo no disparo e na execução. Equivale ao commandMenuItem que o Twenty
// cria para workflows MANUAL.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { listManualAutomations, runManualAutomation } from '../../data/automations.js';
import { Modal } from '../../ui/modal.js';
import { MenuItem } from '../../ui/dropdown.js';
import { enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/automations.js').ManualAutomation} ManualAutomation
 * @typedef {{ objectNameSingular: string, count: number, resolveRecordIds: () => Promise<string[]>, onDone?: () => void }} RecordSelection
 * @typedef {{ automation: ManualAutomation, selection: RecordSelection }} PendingManualRun
 */

// limite do servidor por disparo (QUERY_MAX_RECORDS do Twenty)
export const MAXIMUM_MANUAL_RECORDS = 200;
// a lista muda pouco (só quando alguém ativa ou edita uma automação); evita uma chamada a cada seleção
const CACHE_MILLISECONDS = 60_000;

/** @type {Map<string, { loadedAt: number, promise: Promise<ManualAutomation[]> }>} */
const cacheByObject = new Map();

/**
 * Seleção atual da tabela (ou do kanban), para o menu de comandos oferecer as automações a ela.
 * @type {import('@preact/signals').Signal<RecordSelection | null>}
 */
export const activeRecordSelectionState = signal(null);

/** @type {import('@preact/signals').Signal<PendingManualRun | null>} */
const pendingManualRunState = signal(null);

/** @param {string} objectNameSingular @returns {Promise<ManualAutomation[]>} */
export function loadManualAutomations(objectNameSingular) {
  const cached = cacheByObject.get(objectNameSingular);
  if (cached && Date.now() - cached.loadedAt < CACHE_MILLISECONDS) return cached.promise;
  const promise = listManualAutomations(objectNameSingular).catch(() => /** @type {ManualAutomation[]} */ ([]));
  cacheByObject.set(objectNameSingular, { loadedAt: Date.now(), promise });
  return promise;
}

/** Depois de salvar ou ativar uma automação a lista precisa ser lida de novo. */
export function invalidateManualAutomations() {
  cacheByObject.clear();
}

/** @param {string | null | undefined} objectNameSingular */
export function useManualAutomations(objectNameSingular) {
  const [automations, setAutomations] = useState(/** @type {ManualAutomation[]} */ ([]));
  useEffect(() => {
    if (!objectNameSingular) {
      setAutomations([]);
      return undefined;
    }
    let isCancelled = false;
    loadManualAutomations(objectNameSingular).then((items) => {
      if (!isCancelled) setAutomations(items);
    });
    return () => {
      isCancelled = true;
    };
  }, [objectNameSingular]);
  return automations;
}

/**
 * Pede confirmação e dispara; o diálogo fica na raiz do app, então sobrevive ao fechamento do menu de comandos.
 * @param {ManualAutomation} automation @param {RecordSelection} selection
 */
export function requestManualRun(automation, selection) {
  if (selection.count > MAXIMUM_MANUAL_RECORDS) {
    enqueueSnackbar({ message: t('automations.manual.tooMany', { count: MAXIMUM_MANUAL_RECORDS }), variant: 'warning' });
    return;
  }
  pendingManualRunState.value = { automation, selection };
}

/** @param {PendingManualRun} pending */
async function runPending({ automation, selection }) {
  try {
    const recordIds = await selection.resolveRecordIds();
    if (recordIds.length > MAXIMUM_MANUAL_RECORDS) {
      enqueueSnackbar({ message: t('automations.manual.tooMany', { count: MAXIMUM_MANUAL_RECORDS }), variant: 'warning' });
      return;
    }
    const { runs, skipped } = await runManualAutomation(automation.id, recordIds);
    if (runs.length === 0) {
      enqueueSnackbar({ message: t('automations.manual.noneStarted'), variant: 'warning' });
      return;
    }
    enqueueSnackbar({
      message: t('automations.manual.started', { count: runs.length }),
      detailedMessage: skipped.length > 0 ? t('automations.manual.skipped', { count: skipped.length }) : undefined,
      variant: skipped.length > 0 ? 'warning' : 'success',
    });
    selection.onDone?.();
  } catch (error) {
    showErrorSnackbar(error);
  }
}

/** Diálogo de confirmação, montado uma vez na raiz do app. */
export function ManualRunConfirmation() {
  const pending = pendingManualRunState.value;
  if (!pending) return null;
  const close = () => {
    pendingManualRunState.value = null;
  };
  return html`<${Modal}
    title=${t('automations.manual.confirmTitle', { name: pending.automation.name })}
    subtitle=${[t('automations.manual.confirmSubtitle', { count: pending.selection.count }), pending.automation.description].filter(Boolean).join(' ')}
    confirmLabel=${t('automations.manual.confirm')}
    onClose=${close}
    onConfirm=${() => {
      close();
      runPending(pending);
    }}
  />`;
}

/**
 * Enquanto a barra de seleção está aberta, a seleção também vale no menu de comandos. Não desenha nada.
 * @param {{ objectNameSingular: string, selectedCount: number, resolveSelectedIds: () => Promise<string[]>, onDone: () => void }} props
 */
export function RecordSelectionForCommands({ objectNameSingular, selectedCount, resolveSelectedIds, onDone }) {
  // a tabela recria as funções a cada render; a seleção publicada usa sempre as mais recentes
  const latest = useRef({ resolveSelectedIds, onDone });
  latest.current = { resolveSelectedIds, onDone };
  useEffect(() => {
    /** @type {RecordSelection} */
    const selection = {
      objectNameSingular, count: selectedCount, resolveRecordIds: () => latest.current.resolveSelectedIds(), onDone: () => latest.current.onDone(),
    };
    activeRecordSelectionState.value = selection;
    return () => {
      if (activeRecordSelectionState.peek() === selection) activeRecordSelectionState.value = null;
    };
  }, [objectNameSingular, selectedCount]);
  return null;
}

/**
 * Itens do menu "mais" da barra de seleção (tabela e, quando tiver a barra, kanban).
 * @param {{ objectNameSingular: string, selectedCount: number, resolveSelectedIds: () => Promise<string[]>, onDone: () => void, onPick: () => void }} props
 */
export function SelectionAutomationMenuItems({ objectNameSingular, selectedCount, resolveSelectedIds, onDone, onPick }) {
  const automations = useManualAutomations(objectNameSingular);
  return automations.map((automation) => html`<${MenuItem}
    key=${automation.id}
    icon="IconHandMove"
    text=${t('automations.manual.runOn', { name: automation.name })}
    disabled=${selectedCount > MAXIMUM_MANUAL_RECORDS}
    onClick=${() => {
      onPick();
      requestManualRun(automation, { objectNameSingular, count: selectedCount, resolveRecordIds: resolveSelectedIds, onDone });
    }}
  />`);
}

/**
 * Itens do grupo "Automações" do menu de comandos: para a seleção da tabela, se houver, senão para o registro aberto.
 * @param {{ recordShow: { objectNameSingular: string, recordId: string } | null }} context
 * @returns {{ objectNameSingular: string | null, selection: RecordSelection | null }}
 */
export function commandMenuAutomationTarget({ recordShow }) {
  const selection = activeRecordSelectionState.value;
  if (selection) return { objectNameSingular: selection.objectNameSingular, selection };
  if (!recordShow) return { objectNameSingular: null, selection: null };
  return {
    objectNameSingular: recordShow.objectNameSingular,
    selection: { objectNameSingular: recordShow.objectNameSingular, count: 1, resolveRecordIds: async () => [recordShow.recordId] },
  };
}
